import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ExclusionState } from '../../../services/exclusions/exclusions.types';
import { ExclusionSearchProgress } from '../../../services/exclusions/exclusions.service';
import { ReconcileDecision, ReconcileRow } from '../hooks/useExclusionWorkflow';

export interface ExclusionReconcileTableProps {
  rows: ReconcileRow[];
  unreadable: string[];
  failed: string[];
  counts: {
    absent: number;
    excluded: number;
    explicitly_not_excluded: number;
    toAdd: number;
    toRemove: number;
    unreadable: number;
    failed: number;
  };
  /** Stores a patch here would change that are not in the search. See useExclusionWorkflow. */
  reach: {
    /** Every row found, so sharing is reported up front rather than spotted on one page. */
    found: { stores: string[]; patches: number; truncated: boolean };
    /** Only rows carrying a decision - what a submission would actually change. */
    staged: { stores: string[]; patches: number; truncated: boolean };
  };
  isSearching: boolean;
  progress: ExclusionSearchProgress | null;
  searchError: string | null;
  hasSearched: boolean;
  onDecisionChange: (storeNumber: string, decision: ReconcileDecision) => void;
  /**
   * Sweeps a decision across matching stores, restricted to `scope` when given.
   *
   * The table passes its filtered store list whenever a filter is narrowing the view, so a button offered
   * under a filter cannot reach past what the filter shows. Omitted means every searched store.
   */
  onSelectAllMissing: (scope?: string[]) => void;
  onSelectAllFound: (scope?: string[]) => void;
  onClearDecisions: () => void;
}

/** How each state reads to a user, and how it is coloured. Never a bare enum value. */
const STATE_LABEL: Record<ExclusionState, { label: string; color: 'default' | 'error' | 'warning' }> = {
  absent: { label: 'Not excluded', color: 'default' },
  excluded: { label: 'Excluded', color: 'error' },
  // Visually distinct from "Not excluded" because it is a different fact: somebody set this deliberately.
  explicitly_not_excluded: { label: 'Explicitly not excluded', color: 'warning' },
};

const DECISION_LABEL: Record<ReconcileDecision, string> = {
  none: 'Leave',
  add: 'Exclude',
  remove: 'Un-exclude',
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250];

/**
 * Sort order for the two enum columns.
 *
 * Ranks rather than an alphabetical compare on the enum strings, which would order by spelling and mean
 * nothing. What a user wants from these columns is grouping - every excluded store together - and the
 * filters cover "show me only one of them".
 */
const STATE_RANK: Record<ExclusionState, number> = {
  absent: 0,
  excluded: 1,
  explicitly_not_excluded: 2,
};

const DECISION_RANK: Record<ReconcileDecision, number> = {
  none: 0,
  add: 1,
  remove: 2,
};

type SortColumn = 'store' | 'state' | 'reach' | 'decision';

/**
 * The reconcile review: one row per store, showing what the search found and what will be done about it.
 *
 * Presentational on purpose - every decision lives in useExclusionWorkflow, so this stays testable and
 * the page composes the two.
 *
 * Paginated because the store set can be the whole estate. Rendering 4,700 rows of toggle buttons is not
 * something a browser should be asked to do, and the bulk helpers mean a user rarely needs to see them
 * all anyway.
 */
const ExclusionReconcileTable: React.FC<ExclusionReconcileTableProps> = ({
  rows,
  unreadable,
  failed,
  counts,
  reach,
  isSearching,
  progress,
  searchError,
  hasSearched,
  onDecisionChange,
  onSelectAllMissing,
  onSelectAllFound,
  onClearDecisions,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filter and sort are presentation, so they live here rather than in the workflow hook - which stays
  // about decisions. Same split as StoreOperationsDialog.
  const [storeQuery, setStoreQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<ExclusionState | 'all'>('all');
  const [actionFilter, setActionFilter] = useState<ReconcileDecision | 'all'>('all');
  const [reachFilter, setReachFilter] = useState<'all' | 'shared' | 'unshared'>('all');
  const [orderBy, setOrderBy] = useState<SortColumn>('store');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  /**
   * Whether to give shared patches a column at all.
   *
   * Sharing is common in staging and absent in prod, where a patch is assigned to at most one store - so
   * a column that is always present would be permanently empty for most users, with a header promising
   * something it never shows.
   *
   * Taken from the whole result set rather than the current page, so the table does not gain and lose a
   * column as the user paginates - and so it always agrees with the summary above it. Filters must not
   * feed this either, for the same reason: `reach.found`, never `filteredRows`.
   */
  const showReach = reach.found.patches > 0;

  // Both are gated on the column existing, so a stale answer cannot filter or order by something the
  // table is no longer showing - a new search with no sharing would otherwise hide rows for a reason the
  // user cannot see.
  const isReachFiltered = showReach && reachFilter !== 'all';

  useEffect(() => {
    if (showReach) return;
    setReachFilter('all');
    setOrderBy((previous) => (previous === 'reach' ? 'store' : previous));
  }, [showReach]);

  const isFiltered = storeQuery.trim() !== '' || stateFilter !== 'all' || actionFilter !== 'all' || isReachFiltered;

  const filteredRows = useMemo(() => {
    const term = storeQuery.trim().toLowerCase();

    return rows.filter((row) => {
      if (stateFilter !== 'all' && row.state !== stateFilter) return false;
      if (actionFilter !== 'all' && row.decision !== actionFilter) return false;
      if (isReachFiltered) {
        const isShared = row.otherStoreNumbers.length > 0;
        if (reachFilter === 'shared' && !isShared) return false;
        if (reachFilter === 'unshared' && isShared) return false;
      }
      if (!term) return true;
      return row.storeNumber.toLowerCase().includes(term);
    });
  }, [rows, storeQuery, stateFilter, actionFilter, reachFilter, isReachFiltered]);

  const sortedRows = useMemo(() => {
    const direction = order === 'asc' ? 1 : -1;

    // numeric, so this holds up whether store numbers arrive zero-padded or not.
    const byStore = (a: ReconcileRow, b: ReconcileRow) =>
      a.storeNumber.localeCompare(b.storeNumber, undefined, { numeric: true });

    const primary = (a: ReconcileRow, b: ReconcileRow) => {
      if (orderBy === 'state') return STATE_RANK[a.state] - STATE_RANK[b.state];
      if (orderBy === 'decision') return DECISION_RANK[a.decision] - DECISION_RANK[b.decision];
      if (orderBy === 'reach') {
        return (
          a.otherStoreNumbers.length - b.otherStoreNumbers.length ||
          // Equal known counts, but a truncated list is a floor - so it may well be the larger reach.
          Number(a.otherStoresTruncated) - Number(b.otherStoresTruncated)
        );
      }
      return byStore(a, b);
    };

    // A copy: the rows prop belongs to the caller.
    return [...filteredRows].sort((a, b) => {
      const ranked = primary(a, b) * direction;
      // Tiebreak after the direction flip, so stores read in order within a group whichever way the
      // arrow points.
      return ranked !== 0 ? ranked : byStore(a, b);
    });
  }, [filteredRows, orderBy, order]);

  const pageRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  // A narrower filter can leave the current page past the end of the results.
  useEffect(() => {
    setPage(0);
  }, [storeQuery, stateFilter, actionFilter, reachFilter]);

  const toggleSort = (column: SortColumn) => {
    if (orderBy === column) {
      setOrder((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrderBy(column);
    setOrder('asc');
  };

  /**
   * The stores a bulk helper may touch, or undefined for all of them.
   *
   * From the filtered rows rather than the current page: the filter is what the user asked for, the page
   * they happen to be on is not.
   *
   * Filtering by Action and then sweeping is self-erasing - filter to Leave, click Exclude all missing,
   * and those rows leave the view because they are no longer Leave. Correct, if briefly surprising; the
   * count above the table is what shows where they went.
   */
  const bulkScope = isFiltered ? filteredRows.map((row) => row.storeNumber) : undefined;

  // Under a filter the button has to count what it would actually do, or it would advertise a reach it
  // no longer has.
  const scopedAbsent = isFiltered ? filteredRows.filter((row) => row.state === 'absent').length : counts.absent;
  const scopedExcluded = isFiltered ? filteredRows.filter((row) => row.state === 'excluded').length : counts.excluded;

  const clearFilters = () => {
    setStoreQuery('');
    setStateFilter('all');
    setActionFilter('all');
    setReachFilter('all');
  };

  // A shrinking result set must not leave the view stranded past the end. Off the filtered count, since
  // that is what the pagination is over.
  const lastPage = Math.max(Math.ceil(sortedRows.length / rowsPerPage) - 1, 0);
  const safePage = Math.min(page, lastPage);
  if (safePage !== page) setPage(safePage);

  if (isSearching) {
    const percent =
      progress && progress.storesTotal > 0 ? Math.round((progress.storesSearched / progress.storesTotal) * 100) : 0;

    return (
      <Box sx={{ py: 3 }} data-testid='exclusion-search-progress'>
        <Typography variant='body2' gutterBottom>
          {/* A whole-estate search takes a while by construction: "where missing" has no absence filter,
              so cost scales with the store count rather than the result count. Saying so beats a spinner
              that looks hung. */}
          Searching {progress?.storesTotal ?? 0} stores… {progress?.storesSearched ?? 0} done
        </Typography>
        <LinearProgress variant='determinate' value={percent} />
        <Typography variant='caption' color='text.secondary'>
          Batch {progress?.chunksDone ?? 0} of {progress?.chunksTotal ?? 0}
        </Typography>
      </Box>
    );
  }

  if (searchError) {
    return (
      <Alert severity='error' sx={{ my: 2 }}>
        {searchError}
      </Alert>
    );
  }

  if (!hasSearched) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
        Choose what to exclude, then run the search to see where it applies.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mb: 1 }}>
        <Chip size='small' label={`${counts.excluded} excluded`} color='error' variant='outlined' />
        <Chip size='small' label={`${counts.absent} not excluded`} variant='outlined' />
        {counts.explicitly_not_excluded > 0 && (
          <Chip
            size='small'
            label={`${counts.explicitly_not_excluded} explicitly not excluded`}
            color='warning'
            variant='outlined'
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size='small' onClick={() => onSelectAllMissing(bulkScope)} disabled={!scopedAbsent}>
          Exclude all missing ({scopedAbsent})
        </Button>
        <Button size='small' onClick={() => onSelectAllFound(bulkScope)} disabled={!scopedExcluded}>
          Un-exclude all found ({scopedExcluded})
        </Button>
        <Button size='small' onClick={onClearDecisions} disabled={!counts.toAdd && !counts.toRemove}>
          Clear
        </Button>
      </Stack>

      {(counts.toAdd > 0 || counts.toRemove > 0) && (
        <Alert severity='info' sx={{ mb: 1 }} data-testid='exclusion-decision-summary'>
          Will exclude at {counts.toAdd} store{counts.toAdd === 1 ? '' : 's'} and un-exclude at {counts.toRemove} store
          {counts.toRemove === 1 ? '' : 's'}.
        </Alert>
      )}

      {reach.found.patches > 0 && (
        <Alert
          // Info until the staged decisions would actually reach those stores, then a warning. The
          // overview is worth having either way: sharing should be something the user is told when the
          // results arrive, not something they discover from a chip on whichever page they opened.
          severity={reach.staged.patches > 0 ? 'warning' : 'info'}
          sx={{ mb: 1 }}
          data-testid='exclusion-shared'
        >
          <AlertTitle>
            {reach.found.patches} patch{reach.found.patches === 1 ? '' : 'es'} here also serve
            {reach.found.patches === 1 ? 's' : ''} {reach.found.truncated ? 'at least ' : ''}
            {reach.found.stores.length} store{reach.found.stores.length === 1 ? '' : 's'} you did not search
          </AlertTitle>
          {/* Operations belong to the patch, not to the store, and a patch can be assigned to several
              stores. So a decision made here lands on every store sharing that patch. Not blocking:
              sharing is often legitimate, and the point is that it is visible. */}
          {reach.found.stores.slice(0, 10).join(', ')}
          {reach.found.stores.length > 10 ? ` and ${reach.found.stores.length - 10} more` : ''}.{' '}
          {reach.staged.patches > 0 ? (
            <>
              Your decisions so far would change {reach.staged.patches} of {reach.found.patches === 1 ? 'it' : 'them'},
              reaching {reach.staged.truncated ? 'at least ' : ''}
              {reach.staged.stores.length} of those stores.
            </>
          ) : (
            <>No decision so far touches {reach.found.patches === 1 ? 'it' : 'them'}.</>
          )}
        </Alert>
      )}

      {counts.explicitly_not_excluded > 0 && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          <AlertTitle>Some stores hold a deliberate un-exclude</AlertTitle>
          {counts.explicitly_not_excluded} store{counts.explicitly_not_excluded === 1 ? ' has' : 's have'} an operation
          explicitly setting this to <em>not</em> excluded. The bulk buttons skip them, because excluding and clearing
          the setting are different outcomes — choose per store.
        </Alert>
      )}

      {unreadable.length > 0 && (
        <Alert severity='warning' sx={{ mb: 1 }} data-testid='exclusion-unreadable'>
          <AlertTitle>
            {unreadable.length} patch{unreadable.length === 1 ? '' : 'es'} could not be read
          </AlertTitle>
          {/* Reported rather than shown as "not excluded": the search could not see the patch, which is
              not the same as knowing it lacks the exclusion. Usually a patch whose name falls outside the
              type-suffix convention the query narrows on. */}
          Nothing can be staged for {unreadable.slice(0, 10).join(', ')}
          {unreadable.length > 10 ? ` and ${unreadable.length - 10} more` : ''}.
        </Alert>
      )}

      {failed.length > 0 && (
        <Alert severity='error' sx={{ mb: 1 }} data-testid='exclusion-failed'>
          <AlertTitle>
            {failed.length} store{failed.length === 1 ? '' : 's'} could not be read
          </AlertTitle>
          {/* Neither excluded nor not — nothing was learned, so they are not decisions to make. */}
          Re-run the search to include {failed.slice(0, 10).join(', ')}
          {failed.length > 10 ? ` and ${failed.length - 10} more` : ''}.
        </Alert>
      )}

      {rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
          No stores in the selection carry a patch of this type.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              size='small'
              label='Store number'
              value={storeQuery}
              onChange={(event) => setStoreQuery(event.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'data-testid': 'exclusion-store-filter' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size='small'
              label='Current'
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value as ExclusionState | 'all')}
              sx={{ minWidth: 220 }}
              data-testid='exclusion-state-filter'
            >
              <MenuItem value='all'>All states ({rows.length})</MenuItem>
              <MenuItem value='excluded'>Excluded ({counts.excluded})</MenuItem>
              <MenuItem value='absent'>Not excluded ({counts.absent})</MenuItem>
              <MenuItem value='explicitly_not_excluded'>
                Explicitly not excluded ({counts.explicitly_not_excluded})
              </MenuItem>
            </TextField>
            <TextField
              select
              size='small'
              label='Action'
              value={actionFilter}
              onChange={(event) => setActionFilter(event.target.value as ReconcileDecision | 'all')}
              sx={{ minWidth: 200 }}
              data-testid='exclusion-action-filter'
            >
              <MenuItem value='all'>All actions ({rows.length})</MenuItem>
              <MenuItem value='none'>Leave ({rows.length - counts.toAdd - counts.toRemove})</MenuItem>
              <MenuItem value='add'>Exclude ({counts.toAdd})</MenuItem>
              <MenuItem value='remove'>Un-exclude ({counts.toRemove})</MenuItem>
            </TextField>
            {/* Offered only alongside the column, so the toolbar cannot filter on something the table is
                not showing. Counts come from reach.found, so they agree with the summary alert above. */}
            {showReach && (
              <TextField
                select
                size='small'
                label='Also affects'
                value={reachFilter}
                onChange={(event) => setReachFilter(event.target.value as 'all' | 'shared' | 'unshared')}
                sx={{ minWidth: 200 }}
                data-testid='exclusion-reach-filter'
              >
                <MenuItem value='all'>Any sharing ({rows.length})</MenuItem>
                <MenuItem value='shared'>Shared ({reach.found.patches})</MenuItem>
                <MenuItem value='unshared'>This store only ({rows.length - reach.found.patches})</MenuItem>
              </TextField>
            )}
            {/* What discloses the narrowing. The chips and alerts above describe the whole search, so
                without this the table could be showing 12 of 4,700 rows with nothing saying so. */}
            <Typography variant='body2' color='text.secondary' data-testid='exclusion-filter-count'>
              {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} store
              {rows.length === 1 ? '' : 's'}
            </Typography>
            {isFiltered && (
              <Button size='small' onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Box>

          {filteredRows.length === 0 ? (
            // Distinct from "no patches of this type": there are rows, this view just hides them all.
            // Reporting the empty search here would be a claim about the estate.
            <Typography variant='body2' color='text.secondary' sx={{ py: 2 }} data-testid='exclusion-no-matches'>
              No stores match these filters.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small' stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={orderBy === 'store' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'store'}
                          direction={orderBy === 'store' ? order : 'asc'}
                          onClick={() => toggleSort('store')}
                        >
                          Store
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'state' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'state'}
                          direction={orderBy === 'state' ? order : 'asc'}
                          onClick={() => toggleSort('state')}
                        >
                          Current
                        </TableSortLabel>
                      </TableCell>
                      {showReach && (
                        <TableCell sortDirection={orderBy === 'reach' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'reach'}
                            direction={orderBy === 'reach' ? order : 'asc'}
                            onClick={() => toggleSort('reach')}
                          >
                            Also affects
                          </TableSortLabel>
                        </TableCell>
                      )}
                      <TableCell align='right' sortDirection={orderBy === 'decision' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'decision'}
                          direction={orderBy === 'decision' ? order : 'asc'}
                          onClick={() => toggleSort('decision')}
                        >
                          Action
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((row) => {
                      const state = STATE_LABEL[row.state];
                      return (
                        <TableRow key={row.storeNumber} hover>
                          <TableCell>{row.storeNumber}</TableCell>
                          <TableCell>
                            <Chip size='small' label={state.label} color={state.color} variant='outlined' />
                          </TableCell>
                          {showReach && (
                            <TableCell>
                              {/* Blank rather than "0" when a patch is this store's alone: within a result
                                  set that does have sharing, the cells that matter should be the ones that
                                  stand out. */}
                              {row.otherStoreNumbers.length > 0 && (
                                <Tooltip title={row.otherStoreNumbers.join(', ')}>
                                  <Chip
                                    size='small'
                                    color='warning'
                                    variant='outlined'
                                    label={`${row.otherStoresTruncated ? '≥' : ''}${
                                      row.otherStoreNumbers.length
                                    } store${row.otherStoreNumbers.length === 1 ? '' : 's'}`}
                                    aria-label={`Shared with ${row.otherStoreNumbers.length} other store${
                                      row.otherStoreNumbers.length === 1 ? '' : 's'
                                    }: ${row.otherStoreNumbers.join(', ')}`}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                          <TableCell align='right'>
                            <ToggleButtonGroup
                              size='small'
                              exclusive
                              value={row.decision}
                              onChange={(_event, value) => {
                                // Null arrives when the active button is clicked again; treat that as Leave
                                // rather than as no change, so a decision is always undoable in one click.
                                onDecisionChange(row.storeNumber, (value as ReconcileDecision) ?? 'none');
                              }}
                              aria-label={`Action for store ${row.storeNumber}`}
                            >
                              {row.available.map((decision) => (
                                <ToggleButton key={decision} value={decision} sx={{ py: 0.25, px: 1 }}>
                                  {DECISION_LABEL[decision]}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component='div'
                count={sortedRows.length}
                page={safePage}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default ExclusionReconcileTable;
